import IconCopy from '@bimser/icons/16/copy';
import IconDelete from '@bimser/icons/16/delete';
import IconEdit from '@bimser/icons/16/edit';
import IconEye from '@bimser/icons/16/eye';
import type { Identifier } from 'dnd-core';
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import BCButton from '../../BCButton';
import BCSwitch from '../../BCSwitch';
import Styles from '../assets/styles.scss';
import { IDragItem, IRuleItem } from '../entities/IRuleItem';
import { RequiredRuleId } from '../entities/ruleEngine';

const RuleItem = (props: IRuleItem) => {
	const ref = useRef<HTMLDivElement>(null);
	const [{ handlerId }, drop] = useDrop<IDragItem, void, { handlerId: Identifier | null }>({
		accept: 'RULE_ITEM',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId()
			};
		},
		hover(item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = props.index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			props.moveCard(dragIndex, hoverIndex);
			item.index = hoverIndex;
		}
	});

	const [{ isDragging }, drag] = useDrag({
		type: 'RULE_ITEM',
		item: () => ({ id: props.id, index: props.index }),
		collect: monitor => ({
			isDragging: monitor.isDragging()
		})
	});

	drag(drop(ref));

	const editBtnClicked = () => {
		props.onRuleChange(props.rule, props.rule.id);
	};

	const disableBtnClicked = (checked: boolean) => {
		const clickedRule = props.rule.set('enabled', checked);
		props.onRuleUpdated?.(clickedRule, true);
	};

	const deleteBtnClicked = () => {
		props.onRuleDeleted?.(props.rule);
	};

	const cloneBtnClicked = () => {
		props.onRuleClone?.(props.rule);
	};

	const isRuleReadonly = props.rule.id === RequiredRuleId;

	const opacity = isDragging ? 0 : 1;

	return (
		<div
			className={Styles.ruleItem}
			key={props.rule.id}
			ref={ref}
			data-handler-id={handlerId}
			style={{ opacity }}
		>
			<div className={Styles.title}>
				<BCSwitch
					checked={props.rule.enabled}
					size="small"
					className={Styles.ruleSwitch}
					onChange={checked => {
						disableBtnClicked(checked);
					}}
				/>
				<p>{props.rule.name}</p>
			</div>
			<div className={Styles.extra}>
				<BCButton
					type="text"
					icon={!isRuleReadonly ? <IconEdit /> : <IconEye />}
					onClick={editBtnClicked}
				/>
				{!isRuleReadonly && (
					<BCButton
						type="text"
						icon={<IconCopy />}
						onClick={cloneBtnClicked}
					/>
				)}
				<BCButton
					type="text"
					icon={<IconDelete />}
					onClick={deleteBtnClicked}
				/>
			</div>
		</div>
	);
};

export { RuleItem };
